import { isObject } from '../isObject/isObject.js';
import { isString } from '../isString/isString.js';

var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), member.set(obj, value), value);
var _storage, _local, _session, _a;
class StorageFactory {
  // https://mdn.io/Private_class_fields
  constructor(storageHandler) {
    __privateAdd(this, _storage);
    /**
     * Check whether storage is available.
     */
    __publicField(this, "isAvailable", () => Boolean(__privateGet(this, _storage)));
    /**
     * Retrieve an item from storage.
     *
     * @param key - the name of the item
     */
    __publicField(this, "get", (key) => {
      try {
        const data = JSON.parse(__privateGet(this, _storage)?.getItem(key) ?? "");
        if (!isObject(data)) {
          return null;
        }
        const { value, expires } = data;
        if ((isString(expires) || typeof expires === "number") && /* @__PURE__ */ new Date() > new Date(expires)) {
          this.remove(key);
          return null;
        }
        return value;
      } catch (e) {
        return null;
      }
    });
    /**
     * Save a value to storage.
     *
     * @param key - the name of the item
     * @param value - the data to save
     * @param expires - optional date on which this data will expire
     */
    __publicField(this, "set", (key, value, expires) => __privateGet(this, _storage)?.setItem(
      key,
      JSON.stringify({
        value,
        expires: expires ? new Date(expires) : void 0
      })
    ));
    /**
     * Remove an item from storage.
     *
     * @param key - the name of the item
     */
    __publicField(this, "remove", (key) => __privateGet(this, _storage)?.removeItem(key));
    /**
     * Removes all items from storage.
     */
    __publicField(this, "clear", () => __privateGet(this, _storage)?.clear());
    /**
     * Retrieve an item from storage in its raw state.
     *
     * @param key - the name of the item
     */
    __publicField(this, "getRaw", (key) => __privateGet(this, _storage)?.getItem(key) ?? null);
    /**
     * Save a raw value to storage.
     *
     * @param key - the name of the item
     * @param value - the data to save
     */
    __publicField(this, "setRaw", (key, value) => __privateGet(this, _storage)?.setItem(key, value));
    /**
     * Get key by index.
     * @returns the name of the key at that index (`string`), or `null` (if
     *      index is out of range of if storage is unavailable) - if the index
     *      is out of range, or if storage is not available.
     *
     * Wrapper for the `key` method on the native `Storage` object, with
     * additional check for availability of storage. Note that the _order_ of
     * keys in storage is dependent on user-agent implementation, and so you
     * should not assume that keys will be stored in any particular order (e.g.
     * order of insertion).
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Storage/key
     */
    __publicField(this, "key", (index) => __privateGet(this, _storage)?.key(index) ?? null);
    /**
     * Get the number of items in storage.
     * @returns the number of items in storage, or `null` if storage is
     * 	not available.
     */
    __publicField(this, "length", () => __privateGet(this, _storage)?.length ?? null);
    try {
      const storage2 = window[storageHandler];
      const uid = (/* @__PURE__ */ new Date()).toString();
      storage2.setItem(uid, uid);
      const available = storage2.getItem(uid) == uid;
      storage2.removeItem(uid);
      if (available) {
        __privateSet(this, _storage, storage2);
      }
    } catch (e) {
    }
  }
}
_storage = new WeakMap();
const storage = new (_a = class {
  constructor() {
    __privateAdd(this, _local);
    __privateAdd(this, _session);
  }
  // creating the instance requires testing the native implementation
  // which is blocking. therefore, only create new instances of the factory
  // when it's accessed i.e. we know we're going to use it
  get local() {
    return __privateGet(this, _local) || __privateSet(this, _local, new StorageFactory("localStorage"));
  }
  get session() {
    return __privateGet(this, _session) || __privateSet(this, _session, new StorageFactory("sessionStorage"));
  }
}, _local = new WeakMap(), _session = new WeakMap(), _a)();

export { storage };
